import { useMutation, useQuery } from "@apollo/client";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { PERSON_DETAILS_HEADER_QUERY } from "@/features/person/queries";
import {
  ACHIEVEMENT_TYPES_QUERY,
  ACHIEVEMENTS_FOR_MEMBER_QUERY,
  findAchievementTypeForMember,
  type MembershipAchievementCategoryEnum,
  type MembershipAchievementForMember
} from "@/pages/people/[pgaId]/queries";
import {
  DELETE_ACHIEVEMENT_ATTRIBUTE_MUTATION,
  DELETE_ACHIEVEMENT_CODE_MUTATION,
  DELETE_NATIVE_ACHIEVEMENT_MUTATION
} from "@/pages/people/[pgaId]/mutations";
import { deleteAchievement, resolveDeleteTarget } from "./utils";

interface DeleteAchievementModalProps {
  achievement: MembershipAchievementForMember | null;
  category: MembershipAchievementCategoryEnum;
  onClose(): void;
  open: boolean;
  pgaId: string;
}

export default function DeleteAchievementModal({
  achievement,
  category,
  onClose,
  open,
  pgaId
}: DeleteAchievementModalProps) {
  const { data: personData } = useQuery(PERSON_DETAILS_HEADER_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    skip: !open
  });

  const { data: achievementTypesData, loading: achievementTypesLoading } = useQuery(ACHIEVEMENT_TYPES_QUERY, {
    variables: { category },
    fetchPolicy: "cache-first",
    skip: !open
  });

  const refetchAchievements = {
    refetchQueries: [{ query: ACHIEVEMENTS_FOR_MEMBER_QUERY, variables: { pgaId, achievement: category } }],
    awaitRefetchQueries: true
  };

  const [deleteAchievementCode, { loading: deletingCode }] = useMutation(
    DELETE_ACHIEVEMENT_CODE_MUTATION,
    refetchAchievements
  );
  const [deleteAchievementAttribute, { loading: deletingAttribute }] = useMutation(
    DELETE_ACHIEVEMENT_ATTRIBUTE_MUTATION,
    refetchAchievements
  );
  const [deleteNativeAchievement, { loading: deletingNative }] = useMutation(
    DELETE_NATIVE_ACHIEVEMENT_MUTATION,
    refetchAchievements
  );

  const deleting = deletingCode || deletingAttribute || deletingNative;
  const achievementName = achievement?.title?.trim() || achievement?.code.title?.trim() || "achievement";
  const memberName = personData?.person?.displayName?.trim() || "this member";

  const achievementType = achievement
    ? findAchievementTypeForMember(achievementTypesData?.achievementTypes ?? [], achievement)
    : undefined;
  const deleteResolution =
    achievement && !achievementTypesLoading ? resolveDeleteTarget(achievement, achievementType) : null;
  const deleteTarget = deleteResolution?.target ?? null;
  const deleteUnavailableReason = deleteResolution?.reason ?? null;

  const handleDelete = async () => {
    if (!achievement || !deleteTarget) return;

    const failureMessage = "Unable to delete achievement.";

    try {
      const result = await deleteAchievement({
        pgaId,
        target: deleteTarget,
        mutations: {
          deleteNativeAchievement,
          deleteAchievementCode,
          deleteAchievementAttribute
        }
      });

      if (!result.success) {
        showErrorSnackbar(result.message || failureMessage);
        return;
      }

      showSnackbar("Achievement deleted");
      onClose();
    } catch {
      showErrorSnackbar(failureMessage);
    }
  };

  if (!open || !achievement) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClosed={onClose}
      headline="Delete Achievement"
      actions={
        <div className="flex gap-2">
          <Button variant="text" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="filled"
            theme="error"
            trailingIcon={false}
            icon="delete"
            onClick={handleDelete}
            disabled={deleting || achievementTypesLoading || !deleteTarget}
            testId="confirm-delete-achievement"
          >
            Delete
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <p>
          You are about to delete the achievement {achievementName} for {memberName}. This cannot be undone.
        </p>
        {deleteUnavailableReason && (
          <p className="text-body-medium text-sys-error" data-testid="delete-achievement-unavailable-reason">
            {deleteUnavailableReason}
          </p>
        )}
      </div>
    </Dialog>
  );
}
